import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { listFreeSnapSlots } from '../helper/linearPieceSnapHelper';
var CELL = 110; // WORKSPACE_CELL_SIZE
var PICKER_OFFSET = 6; // px outset so the target frames the slot with breathing room
var buildSlotCandidates = function (manipulative, piece) {
    var tiles = manipulative.filter(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles'; });
    var out = [];
    tiles.forEach(function (m, idx) {
        var td = m.data;
        if (td.isInvisible || td.preview)
            return;
        listFreeSnapSlots(td, piece.orientation).forEach(function (s) {
            var _a;
            out.push({
                tileId: td.id,
                tileNumber: idx + 1,
                side: s.side,
                slot: s.slot,
                center: s.center,
                tileScale: (_a = td.scale) !== null && _a !== void 0 ? _a : 1,
            });
        });
    });
    return out;
};
/**
 * Keyboard / VoiceOver picker for snapping a LinearPiece onto a ColoredTiles
 * edge slot. Mirrors ColoredTilesLinkPicker: overlays a real <button> over every
 * FREE edge slot compatible with the focused piece's orientation inside a
 * role="dialog". Activating a slot dispatches `snapLinearPieceToSlot`, which
 * LinearPiece commits through the same `commitMove` pipeline the mouse drag-snap
 * uses (register + reposition + scale + undo + announce).
 */
var LinearPieceSnapPicker = function () {
    var _a, _b, _c;
    var t = useTranslation().t;
    var manipulative = useContext(DataContext).manipulative;
    var _d = useState(null), focusedPieceId = _d[0], setFocusedPieceId = _d[1];
    var _e = useState(0), activeIdx = _e[0], setActiveIdx = _e[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    useEffect(function () {
        var onOpen = function (data) {
            returnFocusIdRef.current = data.focusedPieceId;
            setFocusedPieceId(data.focusedPieceId);
            setActiveIdx(0);
            announce(t('text.linearPieceSnapPickerOpened'));
        };
        customEventHandler.addEventListener('openLinearPieceSnapPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openLinearPieceSnapPicker', onOpen); };
    }, [t]);
    var focusedItem = focusedPieceId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedPieceId; })
        : undefined;
    var focusedPiece = ((_a = focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.data) === null || _a === void 0 ? void 0 : _a.type) === 'linearPiece' ? focusedItem.data : null;
    var candidates = focusedPiece ? buildSlotCandidates(manipulative, focusedPiece) : [];
    // Auto-close: piece deleted or no free slots left.
    useEffect(function () {
        if (focusedPieceId && (!focusedPiece || candidates.length === 0)) {
            setFocusedPieceId(null);
        }
    }, [focusedPieceId, focusedPiece, candidates.length]);
    // Click-outside closes (but lets the event propagate so canvas interactions work).
    useEffect(function () {
        if (!focusedPieceId)
            return;
        var handleOutside = function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))
                return;
            setFocusedPieceId(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedPieceId]);
    // Auto-focus the first slot button on open (60ms beats closeMenu's 50ms restore).
    useEffect(function () {
        if (!focusedPieceId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedPieceId]);
    if (!focusedPieceId || !focusedPiece || candidates.length === 0)
        return null;
    var closePicker = function () {
        setFocusedPieceId(null);
        var id = returnFocusIdRef.current;
        if (id) {
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        }
    };
    var onSelect = function (cand) {
        customEventHandler.dispatchEvent('snapLinearPieceToSlot', {
            pieceId: focusedPieceId,
            tileId: cand.tileId,
            side: cand.side,
            slot: cand.slot,
        });
        setFocusedPieceId(null);
    };
    var moveFocus = function (delta) {
        var next = (activeIdx + delta + candidates.length) % candidates.length;
        setActiveIdx(next);
        requestAnimationFrame(function () {
            var _a, _b;
            var btn = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button.linearPieceSnapPicker__target')) === null || _b === void 0 ? void 0 : _b[next];
            btn === null || btn === void 0 ? void 0 : btn.focus();
        });
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
        }
        else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(1);
        }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(-1);
        }
    };
    // Piece footprint at the target tile's scale (matches its size once snapped).
    var dims = (_c = (_b = focusedPiece.dimensions) !== null && _b !== void 0 ? _b : focusedPiece.defaultDimensions) !== null && _c !== void 0 ? _c : { width: CELL, height: 20 };
    return (_jsx("div", { ref: containerRef, className: "linearPieceSnapPicker", role: "dialog", "aria-modal": "true", "aria-label": t('text.linearPieceSnapPickerLabel'), onKeyDown: onContainerKeyDown, children: _jsx("div", { role: "application", "aria-label": t('text.linearPieceSnapPickerLabel'), style: { position: 'absolute', inset: 0 }, children: candidates.map(function (cand, idx) {
                var isActive = idx === activeIdx;
                var w = dims.width * cand.tileScale;
                var h = dims.height * cand.tileScale;
                var left = cand.center.x - w / 2;
                var top = cand.center.y - h / 2;
                return (_jsx("button", { ref: idx === 0 ? firstBtnRef : undefined, type: "button", className: "linearPieceSnapPicker__target".concat(isActive ? ' linearPieceSnapPicker__target--active' : ''), tabIndex: 0, "aria-label": t('text.coloredTilesSnapSlotTarget', {
                        edge: t("text.".concat(cand.side)),
                        number: cand.slot + 1,
                        tile: cand.tileNumber,
                    }), style: {
                        position: 'absolute',
                        left: left - PICKER_OFFSET,
                        top: top - PICKER_OFFSET,
                        width: w + PICKER_OFFSET * 2,
                        height: h + PICKER_OFFSET * 2,
                        zIndex: 99999,
                    }, onFocus: function () { return setActiveIdx(idx); }, onClick: function () { return onSelect(cand); }, onKeyDown: function (e) {
                        // JAWS application-mode Enter / keyboard Enter / Space. The
                        // preventDefault cancels the synthesized native click so onSelect
                        // runs exactly once. VoiceOver double-tap → synthetic onClick above.
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelect(cand);
                        }
                    } }, "".concat(cand.tileId, "-").concat(cand.side, "-").concat(cand.slot)));
            }) }) }));
};
export default LinearPieceSnapPicker;
